import type { Track } from '@/lib/db';
import { TrackRepository } from '@/lib/db';

/**
 * Duplicate detection, in two halves, because one flight can arrive twice in
 * two entirely different ways.
 *
 *  - **Identical fixes.** The same export imported twice, or — because the
 *    content hash covers only time and geometry — the KML *and* the CSV of one
 *    flight. Provably the same data, so it is skipped without asking.
 *  - **The same flight, different fixes.** An FR24 export and an EFB GPX of one
 *    trip share no hash at all: different sample rates, different start, one
 *    has taxi time and the other does not. They only look alike in registration
 *    and time.
 *
 * The two get different treatment on purpose. An identical hash is a fact and
 * acting on it loses nothing. An overlap is a guess, and refusing an import on
 * a guess loses a track the pilot wanted — so an overlap is imported and
 * flagged, never blocked. `PLAN.md` §2: the app never silently decides
 * something the pilot did not look at.
 */

/** Below this shared fraction of the shorter track, two flights are unrelated. */
const OVERLAP_THRESHOLD = 0.5;

export type DuplicateKind =
  /** Nothing else in the database looks like this. */
  | 'unique'
  /** Byte-for-byte the same fixes as an existing track. Not imported. */
  | 'identical'
  /** Probably the same flight from another source. Imported, and flagged. */
  | 'overlapping';

export interface DuplicateVerdict {
  kind: DuplicateKind;
  /** The track this one resembles. */
  existing?: Track;
  /** Shared fraction of the shorter of the two tracks, for `overlapping`. */
  overlapFraction?: number;
  /** One line for the import result list. */
  message?: string;
}

/** Fraction of the shorter interval that the two intervals share. */
export function overlapFraction(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): number {
  const a1 = Date.parse(aStart);
  const a2 = Date.parse(aEnd);
  const b1 = Date.parse(bStart);
  const b2 = Date.parse(bEnd);
  if (![a1, a2, b1, b2].every((value) => Number.isFinite(value))) {
    return 0;
  }

  const shared = Math.min(a2, b2) - Math.max(a1, b1);
  if (shared <= 0) {
    return 0;
  }
  const shorter = Math.min(a2 - a1, b2 - b1);
  /*
   * Two zero-length intervals at the same instant. Both are timeless tracks
   * that took the import clock as their times, so sharing an instant says
   * nothing about them being the same flight — and dividing by zero here would
   * otherwise call every timeless import a duplicate of every other.
   */
  if (shorter <= 0) {
    return 0;
  }
  return Math.min(1, shared / shorter);
}

/**
 * Compare a freshly parsed track against what is already stored.
 *
 * @param track  the candidate, not yet written.
 */
export async function checkForDuplicate(
  track: Track,
  repository: TrackRepository = new TrackRepository(),
): Promise<DuplicateVerdict> {
  const identical = await repository.findByContentHash(track.contentHash);
  if (identical !== undefined) {
    return {
      kind: 'identical',
      existing: identical,
      message:
        identical.source === track.source
          ? `Already imported from ${identical.sourceRef ?? 'another file'}.`
          : `Already imported as ${identical.source} from ${identical.sourceRef ?? 'another file'}. Same fixes, different format.`,
    };
  }

  // A track with no times took the import instant for both ends, so a time
  // overlap would compare two clock readings rather than two flights.
  if (track.startTime === track.endTime) {
    return { kind: 'unique' };
  }

  const candidates = await repository.findOverlapping(
    track.startTime,
    track.endTime,
    track.registration,
  );

  let best: Track | undefined;
  let bestFraction = 0;
  for (const candidate of candidates) {
    if (candidate.id === track.id) {
      continue;
    }
    // Without a registration on both sides, a time overlap is only two
    // aircraft airborne at once, which over a busy aerodrome is most of them.
    if (track.registration === undefined || candidate.registration === undefined) {
      continue;
    }
    if (candidate.registration !== track.registration) {
      continue;
    }
    const fraction = overlapFraction(
      track.startTime,
      track.endTime,
      candidate.startTime,
      candidate.endTime,
    );
    if (fraction > bestFraction) {
      best = candidate;
      bestFraction = fraction;
    }
  }

  if (best !== undefined && bestFraction >= OVERLAP_THRESHOLD) {
    return {
      kind: 'overlapping',
      existing: best,
      overlapFraction: bestFraction,
      message: `Shares ${String(Math.round(bestFraction * 100))}% of its time with an existing ${best.registration ?? ''} track from ${best.sourceRef ?? best.source}. Imported anyway. Check whether both are needed.`,
    };
  }

  return { kind: 'unique' };
}

export { OVERLAP_THRESHOLD };
